/**
 * The Datamuse API client shared by the vocabulary tools — browser-side, as the reader
 * types.
 *
 * Datamuse (https://www.datamuse.com/api/) is a free word-finding engine: one endpoint,
 * `/words`, answers "means like", "sounds like", "spelled like" and a dozen lexical
 * relations, with optional definitions, parts of speech, syllable counts, frequencies
 * and pronunciations. It asks public apps to acknowledge it — every page and widget
 * that uses it credits it.
 *
 * The browser does not call Datamuse itself. From early 2027 Datamuse needs an API key
 * on every request, so the widgets ask this site's `/api/words` route, which adds the
 * key (once one is configured; until then it asks keyless), forwards the same query
 * string, and caches answers in the server and at the edge (src/lib/words-proxy.ts). The answer comes back in Datamuse's own JSON shape.
 *
 * Three things live here and nowhere else:
 *
 * - **The query string.** `datamuseQuery` turns a parameter record into a stable,
 *   encoded query string. That string is the cache key and the request — here, on the
 *   server and at the edge — so two widgets asking the same question share one answer.
 * - **The response shape.** Datamuse packs metadata into a `tags` array (`"adj"`,
 *   `"f:12.3"`, `"pron:F L AW1 ER0 "`, `"ipa_pron:…"`, `"syn"`, `"query"`) and
 *   definitions into tab-separated strings (`"adj\tHappy, content. "`). `shapeResults`
 *   validates the JSON and unpacks both into plain fields, so no widget parses a tag.
 * - **The cache.** Answers are kept in memory for the life of the page, so clicking
 *   back to a word you already searched is instant and costs nothing.
 */

/** The site's own route to Datamuse `/words` (src/app/api/words/route.ts). */
export const WORDS_API_URL = "/api/words";

/** Where the credit line links. */
export const DATAMUSE_HOME_URL = "https://www.datamuse.com/api/";

/** Datamuse's part-of-speech codes. `u` is "none of these, or unknown". */
export type PartOfSpeech = "n" | "v" | "adj" | "adv" | "u";

export const PARTS_OF_SPEECH: readonly PartOfSpeech[] = [
  "n",
  "v",
  "adj",
  "adv",
  "u",
];

const POS_CODES: ReadonlySet<string> = new Set(PARTS_OF_SPEECH);

export interface Definition {
  pos: PartOfSpeech;
  text: string;
}

/** One word from a Datamuse answer, with its tags unpacked. */
export interface WordResult {
  word: string;
  /** Datamuse's ranking score. It orders results; it has no other meaning. */
  score: number;
  syllables: number | null;
  /** Most common part of speech first, as Datamuse lists them. */
  pos: PartOfSpeech[];
  /** Occurrences per million words of English (Google Books Ngrams). */
  frequency: number | null;
  definitions: Definition[];
  /** The inflected word's base form, when the definitions are the base form's. */
  headword: string | null;
  /** Tagged `syn`: a WordNet synonym rather than a looser "means like" match. */
  synonym: boolean;
  /**
   * Tagged `prop` (with `md=p`): Datamuse has seen the word as a proper noun. Common
   * words carry it too — "prime", "lyme" — so it means "can be a name", not "is one".
   */
  proper: boolean;
  /** ARPAbet phonemes from the CMU Pronouncing Dictionary, e.g. `F L AW1 ER0`. */
  arpabet: string | null;
  /** Datamuse's own IPA rendering (requested with `ipa=1`). */
  ipa: string | null;
  /** The query echo (`qe=`): the searched word itself, not a result. */
  query: boolean;
}

export type DatamuseParams = Readonly<
  Record<string, string | number | undefined>
>;

/**
 * Encode a request. Empty values are dropped and keys are sorted, so the same question
 * asked twice is the same string — the cache key.
 */
export function datamuseQuery(params: DatamuseParams): string {
  const search = new URLSearchParams();
  for (const key of Object.keys(params).sort()) {
    const value = params[key];
    if (value === undefined || value === "") {
      continue;
    }
    search.set(key, String(value));
  }
  return search.toString();
}

export function wordsApiUrl(query: string): string {
  return `${WORDS_API_URL}?${query}`;
}

/**
 * Read a parameter back out of an answered lookup — `answered` is the newline-joined
 * queries `useDatamuse` reports — so a widget labels results with the word they answer,
 * not the word now in the box.
 */
export function answeredParam(
  answered: string,
  name: string,
  index = 0
): string {
  const query = answered.split("\n")[index] ?? "";
  return new URLSearchParams(query).get(name) ?? "";
}

/* ------------------------------------------------------------------ shaping */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asPos(code: string): PartOfSpeech | null {
  const lower = code.toLowerCase();
  return POS_CODES.has(lower) ? (lower as PartOfSpeech) : null;
}

/** `"adj\tHappy, content. "` → `{ pos: "adj", text: "Happy, content." }`. */
export function parseDefinition(raw: string): Definition | null {
  const tab = raw.indexOf("\t");
  const pos = tab === -1 ? "u" : (asPos(raw.slice(0, tab).trim()) ?? "u");
  const text = (tab === -1 ? raw : raw.slice(tab + 1)).trim();
  return text ? { pos, text } : null;
}

interface TagFields {
  pos: PartOfSpeech[];
  frequency: number | null;
  arpabet: string | null;
  ipa: string | null;
  synonym: boolean;
  proper: boolean;
  query: boolean;
}

const FLAG_TAGS: ReadonlyMap<string, "synonym" | "proper" | "query"> = new Map([
  ["syn", "synonym"],
  ["prop", "proper"],
  ["query", "query"],
]);

function prefixed(tag: string, prefix: string): string | null {
  return tag.startsWith(prefix) ? tag.slice(prefix.length).trim() : null;
}

function readTag(tag: string, fields: TagFields): void {
  const frequency = prefixed(tag, "f:");
  if (frequency !== null) {
    const value = Number(frequency);
    fields.frequency = Number.isFinite(value) ? value : null;
    return;
  }
  const arpabet = prefixed(tag, "pron:");
  if (arpabet !== null) {
    fields.arpabet = arpabet || null;
    return;
  }
  const ipa = prefixed(tag, "ipa_pron:");
  if (ipa !== null) {
    fields.ipa = ipa || null;
    return;
  }
  const flag = FLAG_TAGS.get(tag);
  if (flag) {
    fields[flag] = true;
    return;
  }
  const pos = asPos(tag);
  if (pos && !fields.pos.includes(pos)) {
    fields.pos.push(pos);
  }
}

export function parseTags(tags: unknown): TagFields {
  const fields: TagFields = {
    pos: [],
    frequency: null,
    arpabet: null,
    ipa: null,
    synonym: false,
    proper: false,
    query: false,
  };
  if (Array.isArray(tags)) {
    for (const tag of tags) {
      if (typeof tag === "string") {
        readTag(tag, fields);
      }
    }
  }
  return fields;
}

function shapeDefinitions(defs: unknown): Definition[] {
  if (!Array.isArray(defs)) {
    return [];
  }
  const out: Definition[] = [];
  for (const def of defs) {
    const parsed = typeof def === "string" ? parseDefinition(def) : null;
    if (parsed) {
      out.push(parsed);
    }
  }
  return out;
}

function shapeOne(item: unknown): WordResult | null {
  if (!isRecord(item) || typeof item.word !== "string") {
    return null;
  }
  const word = item.word.trim();
  if (!word) {
    return null;
  }
  const syllables = item.numSyllables;
  return {
    word,
    score: typeof item.score === "number" ? item.score : 0,
    syllables:
      typeof syllables === "number" && syllables > 0 ? syllables : null,
    definitions: shapeDefinitions(item.defs),
    headword:
      typeof item.defHeadword === "string" && item.defHeadword
        ? item.defHeadword
        : null,
    ...parseTags(item.tags),
  };
}

/** Validate a `/words` answer. Anything that is not a word object is dropped. */
export function shapeResults(raw: unknown): WordResult[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: WordResult[] = [];
  for (const item of raw) {
    const shaped = shapeOne(item);
    if (shaped) {
      out.push(shaped);
    }
  }
  return out;
}

/**
 * A name, as far as Datamuse can tell: tagged as a proper noun (`md=p`) and otherwise
 * only ever a noun. "Durkheim" and "Anaheim" qualify; "prime" (also an adjective and a
 * verb) and "crime" (never tagged proper) do not.
 */
export function isName(result: Pick<WordResult, "proper" | "pos">): boolean {
  return result.proper && result.pos.every((pos) => pos === "n");
}

/** Split off the query echo (`qe=`), which Datamuse puts first. */
export function splitEcho(results: readonly WordResult[]): {
  echo: WordResult | null;
  results: WordResult[];
} {
  const echo = results.find((result) => result.query) ?? null;
  return { echo, results: results.filter((result) => !result.query) };
}

/** The first definition for a part of speech, or the first of any when none match. */
export function definitionFor(
  result: Pick<WordResult, "definitions">,
  pos?: PartOfSpeech
): Definition | null {
  if (pos) {
    const match = result.definitions.find((def) => def.pos === pos);
    if (match) {
      return match;
    }
  }
  return result.definitions[0] ?? null;
}

const RE_SPACES = /\s+/g;

/**
 * A word as the user typed it, made into a lookup: trimmed, lower-cased, inner runs of
 * spaces collapsed. Datamuse is case-insensitive, and normalising first keeps "Happy"
 * and "happy " from being two cache entries.
 */
export function normalizeLookup(input: string): string {
  return input.trim().replace(RE_SPACES, " ").toLowerCase();
}

/* -------------------------------------------------------------------- fetch */

export type DatamuseFailure =
  | "network"
  | "rate-limited"
  | "unavailable"
  | "timeout";

export class DatamuseError extends Error {
  readonly reason: DatamuseFailure;

  constructor(reason: DatamuseFailure) {
    super(`Datamuse request failed: ${reason}`);
    this.name = "DatamuseError";
    this.reason = reason;
  }
}

export function failureOf(error: unknown): DatamuseFailure {
  return error instanceof DatamuseError ? error.reason : "network";
}

const TIMEOUT_MS = 15_000;
const CACHE_LIMIT = 300;
const cache = new Map<string, WordResult[]>();

/** An answer already in memory, or undefined. */
export function cachedDatamuse(query: string): WordResult[] | undefined {
  return cache.get(query);
}

function remember(query: string, results: WordResult[]): void {
  cache.set(query, results);
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) {
      cache.delete(oldest);
    }
  }
}

async function request(
  query: string,
  signal: AbortSignal | undefined
): Promise<unknown> {
  const local = new AbortController();
  let timedOut = false;
  const forward = () => local.abort();
  signal?.addEventListener("abort", forward, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    local.abort();
  }, TIMEOUT_MS);
  try {
    const response = await fetch(wordsApiUrl(query), { signal: local.signal });
    // The route answers 429 when Datamuse (or its own per-visitor budget) is limiting
    // lookups, 504 when Datamuse did not answer in time, and 502 (or 503 from the platform) otherwise.
    if (response.status === 429) {
      throw new DatamuseError("rate-limited");
    }
    if (response.status === 504) {
      throw new DatamuseError("timeout");
    }
    if (!response.ok) {
      throw new DatamuseError("unavailable");
    }
    return await response.json().catch(() => {
      throw new DatamuseError("unavailable");
    });
  } catch (error) {
    if (signal?.aborted || error instanceof DatamuseError) {
      throw error;
    }
    throw new DatamuseError(timedOut ? "timeout" : "network");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", forward);
  }
}

/**
 * One `/words` request, answered from memory when it can be. Throws a `DatamuseError`
 * on failure, or the abort error when `signal` aborts — a superseded search stops
 * quietly instead of reporting a failure nobody asked about.
 */
export async function fetchDatamuse(
  query: string,
  signal?: AbortSignal
): Promise<WordResult[]> {
  const hit = cache.get(query);
  if (hit) {
    return hit;
  }
  const results = shapeResults(await request(query, signal));
  remember(query, results);
  return results;
}

/**
 * Run `worker` over `items` with at most `concurrency` requests in flight. Results keep
 * the order of `items`; `onProgress` hears each completion. Resolves to null when
 * `signal` aborts part-way, so a superseded batch never overwrites a newer one.
 */
export async function mapLimited<T, R>(
  items: readonly T[],
  worker: (item: T) => Promise<R>,
  {
    concurrency,
    signal,
    onProgress,
  }: {
    concurrency: number;
    signal: AbortSignal;
    onProgress?: (done: number) => void;
  }
): Promise<R[] | null> {
  const results: R[] = new Array(items.length);
  let next = 0;
  let done = 0;
  async function lane(): Promise<void> {
    while (next < items.length && !signal.aborted) {
      const index = next;
      next += 1;
      results[index] = await worker(items[index]);
      done += 1;
      onProgress?.(done);
    }
  }
  const lanes = Math.max(1, Math.min(concurrency, items.length));
  try {
    await Promise.all(Array.from({ length: lanes }, () => lane()));
  } catch (error) {
    if (signal.aborted) {
      return null;
    }
    throw error;
  }
  return signal.aborted ? null : results;
}
